import { prepareHeaders } from '@/lib/cookie'
import { BASE_URL } from '@/constant'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

export interface Holiday {
  id: number
  name: string
  date: string
  description: string | null
  createdAt: string
  updatedAt: string
}

export interface PaginatedHolidaysResponse {
  success: boolean
  data: Holiday[]
  pagination: {
    total: number
    page: number
    limit: number
    totalPages: number
  }
}

export interface GetHolidaysParams {
  page?: number
  limit?: number
  search?: string
  year?: number | undefined
  sort_by?: string | undefined
  order?: 'ASC' | 'DESC' | undefined
}

export interface CreateHolidayPayload {
  name: string
  date: string
  description?: string
}

export interface UpdateHolidayPayload extends CreateHolidayPayload {
  id: number
}

export interface DeleteHolidayPayload {
  id: number
}

export const holidayApi = createApi({
  reducerPath: 'holidayApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${BASE_URL}/holidays`,
    prepareHeaders: prepareHeaders,
  }),
  tagTypes: ['Holidays'],
  endpoints: builder => ({
    getHolidays: builder.query<PaginatedHolidaysResponse, GetHolidaysParams>({
      query: ({ page = 1, limit = 20, search = '', year, sort_by, order }) => ({
        url: '/',
        params: { page, limit, search, year, sort_by, order },
      }),
      providesTags: ['Holidays'],
    }),

    getHolidayById: builder.query<{ success: boolean; data: Holiday }, number>({
      query: id => `/${id}`,
      providesTags: ['Holidays'],
    }),

    createHoliday: builder.mutation<
      { success: boolean; data: Holiday },
      CreateHolidayPayload
    >({
      query: payload => ({
        url: '/',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Holidays'],
    }),

    updateHoliday: builder.mutation<
      { success: boolean; data: Holiday },
      UpdateHolidayPayload
    >({
      query: ({ id, ...payload }) => ({
        url: `/${id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: ['Holidays'],
    }),

    deleteHoliday: builder.mutation<
      { success: boolean; message: string },
      DeleteHolidayPayload
    >({
      query: ({ id }) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Holidays'],
    }),
  }),
})

export const {
  useGetHolidaysQuery,
  useGetHolidayByIdQuery,
  useCreateHolidayMutation,
  useUpdateHolidayMutation,
  useDeleteHolidayMutation,
} = holidayApi
